import { useState, useEffect } from 'react';
import { X, Calendar, Clock, Zap, AlertTriangle, TrendingUp } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { getPlatformConfig } from '../../../constants/platformLogos';

const SchedulingPanel = ({ selectedResponses, connectedAccounts, onConfirm, onClose }) => {
  const [schedulingMode, setSchedulingMode] = useState('optimal'); // 'optimal', 'custom', 'batch'
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [spacingMinutes, setSpacingMinutes] = useState(30);
  const [optimalTimes, setOptimalTimes] = useState([]);
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    generateOptimalTimes();
    detectConflicts();
  }, [selectedResponses, scheduleDate, scheduleTime, spacingMinutes]);

  const generateOptimalTimes = () => {
    // Generate optimal posting times based on platform and audience activity
    const times = [];
    const platforms = [...new Set(selectedResponses?.map(r => r?.comment?.platform))];

    platforms?.forEach(platform => {
      const config = getPlatformConfig(platform);
      if (!config) return;

      // Platform-specific optimal times (simplified)
      const platformOptimalTimes = {
        twitter: ['09:00', '12:00', '17:00', '20:00'],
        facebook: ['13:00', '15:00', '19:00'],
        instagram: ['11:00', '14:00', '19:00', '21:00'],
        linkedin: ['08:00', '12:00', '17:00'],
        tiktok: ['12:00', '18:00', '21:00']
      };

      const optimalHours = platformOptimalTimes?.[platform] || ['12:00', '18:00'];
      
      optimalHours?.forEach(time => {
        times?.push({
          platform: config?.name,
          time,
          engagementScore: Math.floor(Math.random() * 20) + 75, // 75-95%
          reason: 'Peak audience activity'
        });
      });
    });

    setOptimalTimes(times?.slice(0, 6));
  };

  const detectConflicts = () => {
    if (!scheduleDate || !scheduleTime) {
      setConflicts([]);
      return;
    }

    const detectedConflicts = [];

    // Check for platform overlap
    const platformCounts = {};
    selectedResponses?.forEach(response => {
      const platform = response?.comment?.platform;
      platformCounts[platform] = (platformCounts?.[platform] || 0) + 1;
    });

    Object.entries(platformCounts)?.forEach(([platform, count]) => {
      if (count > 1) {
        detectedConflicts?.push({
          type: 'platform_overlap',
          severity: 'medium',
          platform,
          count,
          message: `${count} responses scheduled for ${platform} at the same time`,
          suggestion: `Space responses ${spacingMinutes} minutes apart to avoid audience fatigue`
        });
      }
    });

    // Check for time-based conflicts (simplified)
    const hour = parseInt(scheduleTime?.split(':')?.[0]);
    if (hour < 6 || hour > 23) {
      detectedConflicts?.push({
        type: 'low_engagement_time',
        severity: 'low',
        message: 'Scheduled time is outside peak engagement hours',
        suggestion: 'Consider scheduling between 8 AM - 11 PM for better engagement'
      });
    }

    setConflicts(detectedConflicts);
  };

  const handleOptimalTimeSelect = (time) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    
    setScheduleDate(tomorrow?.toISOString()?.split('T')?.[0]);
    setScheduleTime(time);
    setSchedulingMode('custom');
  };

  const handleConfirm = () => {
    const schedulingData = {
      mode: schedulingMode,
      date: scheduleDate,
      time: scheduleTime,
      timezone,
      spacingMinutes,
      responseIds: selectedResponses?.map(r => r?.id)
    };

    onConfirm(schedulingData);
  };

  const isValid = () => {
    if (schedulingMode === 'optimal') return true;
    return scheduleDate && scheduleTime;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Schedule Publishing</h3>
            <p className="text-sm text-gray-600 mt-1">
              {selectedResponses?.length} response{selectedResponses?.length > 1 ? 's' : ''} selected
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Scheduling Mode Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-3">Scheduling Mode</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => setSchedulingMode('optimal')}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  schedulingMode === 'optimal' ?'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Zap className={`w-5 h-5 mb-2 ${
                  schedulingMode === 'optimal' ? 'text-blue-600' : 'text-gray-600'
                }`} />
                <p className="font-medium text-gray-900">Optimal Timing</p>
                <p className="text-xs text-gray-600 mt-1">AI-suggested best times</p>
              </button>

              <button
                onClick={() => setSchedulingMode('custom')}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  schedulingMode === 'custom' ?'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Calendar className={`w-5 h-5 mb-2 ${
                  schedulingMode === 'custom' ? 'text-blue-600' : 'text-gray-600'
                }`} />
                <p className="font-medium text-gray-900">Custom Time</p>
                <p className="text-xs text-gray-600 mt-1">Choose specific date/time</p>
              </button>

              <button
                onClick={() => setSchedulingMode('batch')}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  schedulingMode === 'batch' ?'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Clock className={`w-5 h-5 mb-2 ${
                  schedulingMode === 'batch' ? 'text-blue-600' : 'text-gray-600'
                }`} />
                <p className="font-medium text-gray-900">Batch Spacing</p>
                <p className="text-xs text-gray-600 mt-1">Auto-space responses</p>
              </button>
            </div>
          </div>

          {/* Optimal Times Suggestions */}
          {schedulingMode === 'optimal' && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-gray-900">Recommended Posting Times</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {optimalTimes?.map((optimal, index) => (
                  <button
                    key={index}
                    onClick={() => handleOptimalTimeSelect(optimal?.time)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{optimal?.time}</span>
                      <span className="text-sm font-semibold text-green-600">
                        {optimal?.engagementScore}% engagement
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{optimal?.platform}</p>
                    <p className="text-xs text-gray-500 mt-1">{optimal?.reason}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Custom Date/Time Selection */}
          {(schedulingMode === 'custom' || schedulingMode === 'batch') && (
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Date</label>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e?.target?.value)}
                    min={new Date()?.toISOString()?.split('T')?.[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Time</label>
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Timezone</label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Europe/Paris">Paris (CET)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Batch Spacing */}
          {schedulingMode === 'batch' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Spacing Between Responses
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="15"
                  max="120"
                  step="15"
                  value={spacingMinutes}
                  onChange={(e) => setSpacingMinutes(parseInt(e?.target?.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium text-gray-900 w-24">
                  {spacingMinutes} minutes
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Responses will be published {spacingMinutes} minutes apart to prevent audience fatigue
              </p>
            </div>
          )}

          {/* Conflict Detection */}
          {conflicts?.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                <h4 className="font-medium text-gray-900">Scheduling Conflicts Detected</h4>
              </div>
              <div className="space-y-2">
                {conflicts?.map((conflict, index) => (
                  <div 
                    key={index}
                    className={`p-3 rounded-lg border ${
                      conflict?.severity === 'high' ? 'bg-red-50 border-red-200' :
                      conflict?.severity === 'medium'? 'bg-orange-50 border-orange-200' : 'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900">{conflict?.message}</p>
                    <p className="text-xs text-gray-600 mt-1">{conflict?.suggestion}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Response Preview */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Responses to Schedule</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {selectedResponses?.map((response, index) => {
                const platformConfig = getPlatformConfig(response?.comment?.platform);
                return (
                  <div key={response?.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    {platformConfig && (
                      <img 
                        src={platformConfig?.logo} 
                        alt={platformConfig?.alt}
                        className="w-5 h-5 object-contain"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">
                        {response?.finalResponse || response?.suggestedResponse}
                      </p>
                    </div>
                    {schedulingMode === 'batch' && (
                      <span className="text-xs text-gray-600 whitespace-nowrap">
                        +{index * spacingMinutes}m
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-between">
          <Button
            onClick={onClose}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Schedule {selectedResponses?.length} Response{selectedResponses?.length > 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SchedulingPanel;